import { useMemo } from "react";

/**
 * Frontend-only password strength heuristic. Buckets:
 *   0 → empty
 *   1 → weak    (< 8 chars, or dictionary hit)
 *   2 → fair    (8+ chars, missing one class)
 *   3 → strong  (10+ chars, 3 classes)
 *   4 → excellent (12+ chars, all 4 classes)
 *
 * Not a security guarantee — pairs with server-side minimum length. Just
 * nudges users away from `password123` before they hit submit.
 */

const COMMON = new Set([
  "password", "password1", "password123", "12345678", "qwerty",
  "letmein", "welcome", "iloveyou", "admin", "abc12345",
  "monkey", "dragon", "master", "sunshine", "princess",
]);

export function scorePassword(pw) {
  if (!pw) return { score: 0, label: "", pct: 0 };
  if (COMMON.has(pw.toLowerCase())) {
    return { score: 1, label: "Weak — too common", pct: 20 };
  }
  const len = pw.length;
  let classes = 0;
  if (/[a-z]/.test(pw)) classes++;
  if (/[A-Z]/.test(pw)) classes++;
  if (/[0-9]/.test(pw)) classes++;
  if (/[^a-zA-Z0-9]/.test(pw)) classes++;

  let score = 1;
  let label = "Weak";
  if (len >= 8 && classes >= 2) { score = 2; label = "Fair"; }
  if (len >= 10 && classes >= 3) { score = 3; label = "Strong"; }
  if (len >= 12 && classes === 4) { score = 4; label = "Excellent"; }
  if (len < 8) { score = 1; label = "Weak — too short"; }

  return { score, label, pct: score * 25 };
}

const COLORS = ["bg-white/5", "bg-rose-600", "bg-amber-400", "bg-emerald-400", "bg-emerald-300"];
const TEXT   = ["text-zinc-500", "text-rose-400", "text-amber-300", "text-emerald-400", "text-emerald-300"];

export default function PasswordStrengthMeter({ password, testid = "password-strength-meter" }) {
  const { score, label, pct } = useMemo(() => scorePassword(password || ""), [password]);
  if (!password) return null;
  return (
    <div className="mt-2" data-testid={testid}>
      <div className="h-1 bg-white/5 overflow-hidden" role="progressbar" aria-valuenow={score} aria-valuemin={0} aria-valuemax={4}>
        <div
          className={`h-full transition-all duration-300 ${COLORS[score]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className={`text-[10px] uppercase tracking-widest mt-1.5 ${TEXT[score]}`} data-testid={`${testid}-label`}>
        {label}
      </div>
    </div>
  );
}
